import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { toast } from "react-toastify";
import { GIFT_ANIMATION_POSITIONS } from "./constants";
import { AnimatedGift, AvatarPosition, AvatarPositions, DeviceBreakpoints } from "./types";
import { giftData } from "../../../../../Api/configs";
import { AppImages } from "../../../../../utility/AppImages";
import { useLiveStream } from "../../../../../context/LiveStreamContext";


export const useDeviceBreakpoints = (): DeviceBreakpoints => {
  const deviceSize = useDeviceSize();

  return useMemo(() => ({
    isMobile: deviceSize === DEVICE_SIZES.XS ||
      deviceSize === DEVICE_SIZES.SM ||
      deviceSize === DEVICE_SIZES.MD ||
      deviceSize === DEVICE_SIZES.LG,
    isTablet: deviceSize === DEVICE_SIZES.MD || deviceSize === DEVICE_SIZES.LG,
    isDesktop: deviceSize === DEVICE_SIZES.XL
  }), [deviceSize]);
};

export const useGiftAnimations = (
  selectedPlayer: string,
  audienceData: any,
  avatarPositions: AvatarPositions
) => {
  const [animatedGifts, setAnimatedGifts] = useState<AnimatedGift[]>([]);
  const { isMobile } = useDeviceBreakpoints();

  const removeAnimatedGift = useCallback((giftId: string) => {
    setAnimatedGifts((prev) => prev.filter((gift) => gift.id !== giftId));
  }, []);

  const triggerGiftAnimation = useCallback(
    (giftItem: any) => {
      const giftId = `gift-${Date.now()}-${Math.random()}`;
      const matchingGift = giftData.find(
        (gift) => gift.giftName === giftItem.giftName
      );

      const getPlayerIndex = () => {
        if (selectedPlayer === "both") return 2;
        const player1 = audienceData?.participantDetails?.[0]?.userID;
        const player2 = audienceData?.participantDetails?.[1]?.userID;
        if (selectedPlayer === player1) return 0;
        if (selectedPlayer === player2) return 1;
        return 2;
      };

      const playerIndex = getPlayerIndex();
      const positions = isMobile ? GIFT_ANIMATION_POSITIONS.MOBILE : GIFT_ANIMATION_POSITIONS.DESKTOP;

      let startPosition = positions.START;
      let endPosition: AvatarPosition;

      if (avatarPositions.player1 && playerIndex === 0) {
        endPosition = avatarPositions.player1;
      } else if (avatarPositions.player2 && playerIndex === 1) {
        endPosition = avatarPositions.player2;
      } else if (avatarPositions.both && playerIndex === 2) {
        endPosition = avatarPositions.both;
      } else {
        endPosition = playerIndex === 0 ? positions.PLAYER_1 :
          playerIndex === 1 ? positions.PLAYER_2 : positions.BOTH;
      }

      const newAnimatedGift: AnimatedGift = {
        id: giftId,
        giftImage: matchingGift?.gift || AppImages.icWhiteDollarIcon,
        startPosition,
        endPosition,
        playerIndex,
      };

      setAnimatedGifts((prev) => [...prev, newAnimatedGift]);
    },
    [selectedPlayer, audienceData?.participantDetails, avatarPositions, isMobile]
  );

  return {
    animatedGifts,
    triggerGiftAnimation,
    removeAnimatedGift,
  };
};

export const useStreamState = () => {
  const { audienceData, eventStartTime } = useLiveStream();

  return useMemo(() => {
    const currentDateTime = new Date();
    const eventDateTime = eventStartTime ? new Date(eventStartTime) : null;
    const isWithinValidTime = eventDateTime &&
      !isNaN(eventDateTime.getTime()) &&
      currentDateTime.getTime() >= eventDateTime.getTime();

    const isOrganiserStreamCompleted =
      audienceData?.eventDetails?.streamStatus?.organiser === "completed";

    // Check if organiser is currently streaming live
    const isOrganiserStreamLive =
      audienceData?.eventDetails?.streamStatus?.organiser === "live";

    // Check if challenger is currently streaming live
    const isChallengerStreamLive =
      audienceData?.eventDetails?.streamStatus?.challenger === "live";

    // Streaming is available if:
    // 1. Event status is "live" OR organiser stream is live OR challenger stream is live
    // 2. AND organiser stream is not completed
    // 3. AND we're within valid time (past event start time)
    const isEventLive = audienceData?.eventDetails?.status === "live";
    const isAnyStreamLive = isOrganiserStreamLive || isChallengerStreamLive || isEventLive;

    const isStreamingAvailable =
      isAnyStreamLive &&
      !isOrganiserStreamCompleted &&
      isWithinValidTime;

    return {
      currentDateTime,
      eventDateTime,
      isWithinValidTime,
      isOrganiserStreamCompleted,
      isOrganiserStreamLive,
      isChallengerStreamLive,
      isStreamingAvailable,
    };
  }, [audienceData, eventStartTime]);
};

export const useErrorHandler = () => {
  const showError = useCallback((message: string) => {
    console.error(message);
    toast.warning(message);
  }, []);

  const showSuccess = useCallback((message: string) => {
    toast.success(message);
  }, []);

  return { showError, showSuccess };
};

export const useAvatarPositions = (onAvatarLayout?: (positions: AvatarPositions) => void) => {
  const [avatarPositions, setAvatarPositions] = useState<AvatarPositions>({});
  const player1AvatarRef = useRef<any>(null);
  const player2AvatarRef = useRef<any>(null);
  const bothAvatarRef = useRef<any>(null);

  const measureAvatarPositions = useCallback(() => {
    const positions: AvatarPositions = {};

    const checkAllPositionsMeasured = (pos: AvatarPositions) => {
      if (pos.player1 && pos.player2 && pos.both) {
        setAvatarPositions(pos);
        onAvatarLayout?.(pos);
      }
    };

    if (player1AvatarRef.current) {
      player1AvatarRef.current.measure((_x: number, _y: number, width: number, height: number, pageX: number, pageY: number) => {
        positions.player1 = { x: pageX + width / 2, y: pageY + height / 2 };
        checkAllPositionsMeasured(positions);
      });
    }

    if (player2AvatarRef.current) {
      player2AvatarRef.current.measure((_x: number, _y: number, width: number, height: number, pageX: number, pageY: number) => {
        positions.player2 = { x: pageX + width / 2, y: pageY + height / 2 };
        checkAllPositionsMeasured(positions);
      });
    }

    if (bothAvatarRef.current) {
      bothAvatarRef.current.measure((_x: number, _y: number, width: number, height: number, pageX: number, pageY: number) => {
        positions.both = { x: pageX + width / 2, y: pageY + height / 2 };
        checkAllPositionsMeasured(positions);
      });
    }
  }, [onAvatarLayout]);

  return {
    avatarPositions,
    player1AvatarRef,
    player2AvatarRef,
    bothAvatarRef,
    measureAvatarPositions,
  };
};

export const useCleanupTimers = (...timerRefs: React.MutableRefObject<NodeJS.Timeout | null>[]) => {
  useEffect(() => {
    return () => {
      timerRefs.forEach(timerRef => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      });
    };
  }, []);
};

export const usePlayerData = (selectedPlayer: string) => {
  const { audienceData } = useLiveStream();
  const [playerData, setPlayerData] = useState<any[]>([]);

  useEffect(() => {
    if (audienceData?.participantDetails) {
      const transformedData = audienceData.participantDetails.map(
        (participant: any, index: number) => ({
          id: index + 1,
          userID: participant.userID,
          image: participant.profilePicture || "",
          name: participant.userID || "Unknown",
          type: `Player ${index + 1}`,
          watch: participant.userID === selectedPlayer ? "watching" : "watch",
        })
      );

      const combinedPlayerData = {
        id: transformedData.length + 1,
        isCombined: true,
      };

      setPlayerData([...transformedData, combinedPlayerData]);
    }
  }, [audienceData, selectedPlayer]);

  return { playerData, setPlayerData };
};